import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { createPortal } from "react-dom"
import cx from "classnames"
import { IoChevronDownOutline } from "react-icons/io5"
import SimpleBarReact from "simplebar-react"
import "simplebar/dist/simplebar.min.css"
import BlurHandler from "./BlurHandler"
import { useDebouncedEffect } from "../utils/utils"

// Apple trackpads and Magic Mice already coast natively — never touch scrolling there
const IS_APPLE =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad|iPod/i.test(navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || "")

// Strictly a notched mouse wheel: line-based deltas, or the legacy 120-per-notch wheelDelta.
// Anything ambiguous (trackpads, free-spin wheels) keeps native scrolling.
const isNotchedWheel = (e) =>
    e.deltaX === 0 && !e.ctrlKey && (e.deltaMode === 1 || (e.wheelDeltaY && Math.abs(e.wheelDeltaY) % 120 === 0))

const getVal = (item) => item?.label ?? item?.title ?? (typeof item === "string" ? item : "")

const DropDown = ({
    className,
    center,
    clear,
    autoFocus,
    onChange,
    onSelect,
    style,
    getRef,
    getContainerRef,
    getInputRef,
    open: controlledOpen,
    placeHolder,
    inputProps: passedInputProps = {},
    maxItems = 50,
    autoHighlight = false,
    getItemValue,
    wrapperStyle,
    onMenuVisibilityChange,
    items = [],
    template,
    menuWidth,
    noEmptyMenu = false,
    noResultsText = "No results found...",
    value = "",
    clearOnSelect,
    searchable = true,
    shouldItemRender,
    selectOnBlur = true, // default to true like original
    usePortal = false,
    portalTarget,
    menuPosition = "bottom",
    menuOffset = 4,
    // A portalled menu sits above the page, but not above a panel that is higher
    // still (the mAX panel is 99999999): such a caller raises it.
    menuZIndex = 9999,
    // Replaces the menu's height cap (a number, or "none" for a menu that never scrolls).
    // Inline, because the portalled menu's computed cap is inline too.
    menuMaxHeight,
    // Classes for the menu itself: a portalled menu is not inside className.
    menuClassName,
    onBlur,
}) => {
    const [isOpen, setIsOpen] = useState(controlledOpen)
    const [inputValue, setInputValue] = useState(value)
    // True only while the user is actively typing/pasting into the field.
    // A displayed value that was set externally (via the `value` prop or by
    // picking an item) isn't a search query and may not match any item's
    // label verbatim — filtering on it would wrongly show "No results found".
    const [isSearching, setIsSearching] = useState(false)
    const containerRef = useRef(null)
    const inputRef = useRef(null)
    const scrollRef = useRef(null)
    const [menuStyle, setMenuStyle] = useState({})
    // Last thing the user typed. Parents commonly mirror `onChange` back into
    // `value`, and that echo must not be mistaken for an externally set value.
    const lastTypedRef = useRef(null)

    const isControlled = false
    const finalOpen = isControlled ? controlledOpen : isOpen

    const filteredItems = useMemo(() => {
        if (!items?.length) return []

        // Non-searchable dropdowns act like a plain select: never filter by the
        // displayed value, since it may not match the item labels verbatim.
        if (!searchable) return items.slice(0, maxItems)

        // Nothing typed yet (or the value was set externally, not searched for)
        // → browse the full list instead of filtering against a value that may
        // not match any item's label.
        if (!isSearching || !inputValue) return items.slice(0, maxItems)

        return items
            .filter((item) => {
                if (typeof shouldItemRender === "function") return shouldItemRender(item, inputValue)
                const label = getItemValue ? getItemValue(item) : getVal(item)
                return label?.toLowerCase().includes(inputValue.toLowerCase())
            })
            .slice(0, maxItems)
    }, [items, inputValue, maxItems, searchable, isSearching])

    const calculateMenuPosition = useCallback(() => {
        if (!containerRef.current) return {}

        const rect = containerRef.current.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight

        const width = menuWidth || rect.width
        const DEFAULT_MAX_HEIGHT = 300
        const PADDING = 8 // Space from viewport edges

        let top = 0
        let maxHeight = DEFAULT_MAX_HEIGHT

        if (menuPosition === "top") {
            // Space available between top of viewport and trigger element
            const availableSpaceAbove = rect.top - menuOffset - PADDING
            maxHeight = Math.min(DEFAULT_MAX_HEIGHT, Math.max(100, availableSpaceAbove))

            // Anchored by its bottom edge, so a menu shorter than maxHeight still
            // sits right on the trigger instead of floating above it.
            return {
                position: "fixed",
                bottom: viewportHeight - rect.top + menuOffset,
                left: Math.max(Math.min(rect.left, viewportWidth - width - PADDING), PADDING),
                width,
                maxHeight: menuMaxHeight ?? maxHeight,
                overflowY: "hidden",
                zIndex: menuZIndex,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }
        } else {
            // Space available between bottom of trigger element and bottom of viewport
            const availableSpaceBelow = viewportHeight - rect.bottom - menuOffset - PADDING
            maxHeight = Math.min(DEFAULT_MAX_HEIGHT, Math.max(100, availableSpaceBelow))

            top = rect.bottom + menuOffset
        }

        // Horizontal bounds clamping
        let left = rect.left
        if (left + width > viewportWidth) {
            left = viewportWidth - width - PADDING
        }
        left = Math.max(left, PADDING)

        return {
            position: "fixed",
            top: top - 4,
            left,
            width,
            maxHeight: menuMaxHeight ?? maxHeight,
            overflowY: "hidden",
            zIndex: menuZIndex,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }
    }, [menuWidth, menuPosition, menuOffset, menuZIndex, menuMaxHeight])

    useDebouncedEffect(
        () => {
            onMenuVisibilityChange?.(finalOpen)
        },
        [finalOpen, onMenuVisibilityChange],
        10,
    )

    useEffect(() => {
        if (!usePortal || !finalOpen) return
        const update = () => setMenuStyle(calculateMenuPosition())
        update()
        window.addEventListener("scroll", update, true)
        window.addEventListener("resize", update)
        return () => {
            window.removeEventListener("scroll", update, true)
            window.removeEventListener("resize", update)
        }
    }, [usePortal, finalOpen, calculateMenuPosition])

    // Auto focus
    useEffect(() => {
        if (autoFocus && inputRef.current) {
            setTimeout(() => inputRef.current.focus(), 20)
        }
    }, [autoFocus])

    // Sync controlled value
    useEffect(() => {
        const next = value || ""
        // The parent just echoed our own keystroke back — keep searching,
        // otherwise every character would reset the filter to the full list.
        if (next === lastTypedRef.current) return
        lastTypedRef.current = null
        setInputValue(next)
        setIsSearching(false)
    }, [value])

    // Close on outside click / blur
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !e.target.closest(".dna-dd-menu")) {
                if (!isControlled) setIsOpen(false)
                onMenuVisibilityChange?.(false)
            }
        }

        if (finalOpen) {
            document.addEventListener("mousedown", handleClickOutside)
            return () => document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [finalOpen, isControlled, onMenuVisibilityChange])

    const handleInputChange = (e) => {
        const newValue = e.target.value
        lastTypedRef.current = newValue
        setInputValue(newValue)
        setIsSearching(true)
        onChange?.(newValue, e)
        if (!isControlled) setIsOpen(true)
    }

    const handleSelect = (item) => {
        const val = getItemValue ? getItemValue(item) : getVal(item)
        lastTypedRef.current = null
        setInputValue(clearOnSelect ? "" : val)
        setIsSearching(false)
        onSelect?.(val, item)

        if (!isControlled) setIsOpen(false)
    }

    const handleBlur = (e) => {
        // If focus is moving to the menu, don't close
        if (e.relatedTarget?.closest?.(".dna-dd-menu")) {
            return
        }

        setTimeout(() => {
            try {
                if (!isControlled && selectOnBlur) {
                    setIsOpen(false)
                    onMenuVisibilityChange?.(false)
                }
            } catch (err) {}
        }, 150)

        if (onBlur) {
            onBlur(e)
        }
        if (passedInputProps?.onBlur) {
            passedInputProps?.onBlur?.(e)
        }
    }

    // Also update the onFocus to handle controlled state better
    const handleFocus = () => {
        if (!isControlled) {
            setIsOpen(true)
            onMenuVisibilityChange?.(true)
        }
    }

    const cn = cx("dna-drop-down", className, {
        center,
        clear,
        active: finalOpen,
    })

    const menuContent =
        filteredItems.length > 0 ? (
            filteredItems.map((item, index) => {
                const isHighlighted = autoHighlight && index === 0
                return (
                    <div
                        key={index}
                        className={template ? "" : isHighlighted ? "dna-dd-item active" : "dna-dd-item"}
                        onClick={() => handleSelect(item)}
                        onMouseDown={(e) => e.preventDefault()} // Prevent blur before click
                    >
                        {template ? template(item, inputValue, isHighlighted) : getVal(item)}
                    </div>
                )
            })
        ) : !noEmptyMenu ? (
            <div className="dna-dd-item dna-light-text" style={{ paddingLeft: 5 }}>
                {noResultsText}
            </div>
        ) : null

    // A touch of inertia for mouse wheels only
    const hasMenu = Boolean(finalOpen && menuContent)
    useEffect(() => {
        const node = scrollRef.current
        if (!hasMenu || !node || IS_APPLE) return
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return

        let target = null
        let raf = null

        const step = () => {
            const diff = target - node.scrollTop
            if (Math.abs(diff) < 0.5) {
                node.scrollTop = target
                target = null
                raf = null
                return
            }
            node.scrollTop += diff * 0.12
            raf = requestAnimationFrame(step)
        }

        const onWheel = (e) => {
            if (!isNotchedWheel(e)) return
            const max = node.scrollHeight - node.clientHeight
            const from = target ?? node.scrollTop
            // At the edge: let the page scroll as it does today
            if ((e.deltaY < 0 && from <= 0) || (e.deltaY > 0 && from >= max)) return

            e.preventDefault()
            const delta = e.deltaMode === 1 ? e.deltaY * 40 : e.deltaY
            target = Math.max(0, Math.min(max, from + delta))
            if (!raf) raf = requestAnimationFrame(step)
        }

        node.addEventListener("wheel", onWheel, { passive: false })
        return () => {
            node.removeEventListener("wheel", onWheel)
            if (raf) cancelAnimationFrame(raf)
        }
    }, [hasMenu])

    const menuLeft = useMemo(() => {
        if (!isOpen || !menuContent || !menuWidth || !containerRef.current) {
            return 0
        }

        const rect = containerRef.current.getBoundingClientRect()
        const viewportWidth = window.innerWidth

        // How much it's being cut off on the right side
        const overflow = rect.left + menuWidth - viewportWidth

        // If it's overflowing, return the negative overflow amount
        // This will shift the menu left by exactly how much it's cut off
        if (overflow > 0) {
            return -overflow
        }

        return 0
    }, [menuWidth, containerRef, menuContent, isOpen])

    const menuElement = menuContent && (
        <div
            className={cx("dna-dd-menu no-blur", menuClassName)}
            style={
                usePortal
                    ? menuStyle
                    : {
                          top: 38,
                          transform: menuLeft < 0 ? `translateX(${menuLeft}px)` : undefined,
                          width: menuWidth,
                          maxHeight: menuMaxHeight,
                      }
            }
            // Grabbing the scrollbar must not blur the input and close the menu
            onMouseDown={(e) => e.preventDefault()}
        >
            {/* Scrolls inside the menu's max-height, whichever stylesheet sets it */}
            <SimpleBarReact className="dna-dd-menu-scroll" scrollableNodeProps={{ ref: scrollRef }}>{menuContent}</SimpleBarReact>
        </div>
    )

    const handleInputClick = () => {
        if (!isControlled) {
            setIsOpen(true)
            onMenuVisibilityChange?.(true)
        }
    }

    return (
        <div className={cn} style={style} ref={mergeRefs(containerRef, getContainerRef, getRef)}>
            <div style={wrapperStyle}>
                <input
                    ref={mergeRefs(inputRef, getInputRef)}
                    {...passedInputProps}
                    placeholder={placeHolder}
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    data-x-dd="true"
                    autoComplete="new-password"
                    onClick={handleInputClick}
                />

                <span className="x-dd-dd-dd-icon" onClick={() => !isControlled && setIsOpen((v) => !v)}>
                    <IoChevronDownOutline />
                </span>
            </div>

            {finalOpen &&
                (usePortal
                    ? createPortal(menuElement, portalTarget || document.querySelector("#dna-body"))
                    : menuElement)}
        </div>
    )
}

const mergeRefs =
    (...refs) =>
    (node) => {
        refs.forEach((ref) => {
            if (typeof ref === "function") ref(node)
            else if (ref) ref.current = node
        })
    }

export default DropDown
